/*
  @file MapFunctions.vue
  @description Clase que almacena la lógica para trabajar dentro del mapa, esta clase permite
    crear,modificar,actualizar marcadores de tipo marker y L, así como administrar la(s) capa(s)
    que se creen dentro del mismo mapa.

  @author Adrian Zayas <adrianzayas@unindetec.edu.mx>
  @date 06-03-2025
  @version 1.0.0
*/
import L from "leaflet";

/**
 * @var map
 * @brief Objeto para almacenar la referencia del mapa desde Map.vue
 */
let map = null;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-black-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon-black.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  spritesheet: require("leaflet-draw/dist/images/spritesheet.png"),
});

/** Clase para crear un icono de marcador personalizado. */
let MarkerIcon = L.Icon.extend({
  options: {
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    iconSize: [40, 76],
    shadowSize: [45, 45],
    iconAnchor: [20, 57],
    shadowAnchor: [15, 45],
    popupAnchor: [0, -32],
  },
});
/** Icono para el marcador de dispositivo estático del dispositivo */
const staticDeviceIcon = new MarkerIcon({
  iconUrl: "/assets/img/dispositivo-estatico-redondo.svg",
});
/** Icono para el marcador fuera de linea del dispositivo. */
const offlineDeviceIcon = new MarkerIcon({
  iconUrl: "/assets/img/dispositivo-fuera-de-linea-redondo.svg",
});
/** Icono para el marcador de dispositivo en movimiento. */
const movementDeviceIcon = new MarkerIcon({
  iconUrl: "/assets/img/dispositivo-mov-redondo.svg",
});
/** Icono para el marcador de ubicacion guardada */
const saveLocationIcon = new MarkerIcon({
  iconUrl: "/assets/img/ubicacion-guardada-redondo.svg",
});

/** Icono para la alerta de geocerca */
const geofenceIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorGeocerca.svg",
});
/** Icono para la alerta en movimiento */
const moveIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorMovimiento.svg",
});
/** Icono para la alerta de verlocidad */
const speedIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorVelocidad.svg",
});
/** Icono para la alerta de impacto */
const shockIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorImpacto.svg",
});
/** Icono para la alerta de batería baja */
const bateryIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorBateria.svg",
});
/** Icono para la alerta de auxilio */
const sosIcon = new MarkerIcon({
  iconUrl: "/assets/img/marcadorAuxilio.svg",
});

const iconStart = new MarkerIcon({
  iconUrl: "/assets/img/iconoInicio.svg",
});
const iconEnd = new MarkerIcon({
  iconUrl: "/assets/img/iconoFinal.svg",
});

/**
 * Método que asigna un icono a un marcador dependiendo de su estado, es aplicable para dispositivos y alertas.
 * @param estado - Estado del dispositivo o alerta
 * @returns {L.Icon} - Icono correspondiente al estado
 */
export function getTypeIcon(estado) {
  switch (estado) {
    case "EST":
      return staticDeviceIcon;
    case "MOV":
      return movementDeviceIcon;
    case "MARKER":
      return saveLocationIcon;
    case "Geocerca":
      return geofenceIcon;
    case "Movimiento":
      return moveIcon;
    case "Velocidad":
      return speedIcon;
    case "Impacto":
      return shockIcon;
    case "Batería baja":
      return bateryIcon;
    case "Auxilio":
      return sosIcon;
    case "START":
      return iconStart;
    case "END":
      return iconEnd;
    case "OFF":
    default:
      return offlineDeviceIcon;
  }
}
/** 
 * @brief Método encargado de asignar la referencia del mapa de leaflet. 
 * @param mapReference - Referencia al objeto del mapa de leaflet 
 */
export function setMapReference(mapReference) {
  map = mapReference;
}
/**
 * Método encargado de renderizar una entidad en el mapa.
 * Las entidades pueden ser marcadores, geocercas o dispositivos.
 * @param entity - Objeto que contiene la información de la geocerca.
 * @param popup - Objeto que contiene la información del popup.
 * @param open - Booleano que indica si el popup se debe abrir o no.
 * @returns Objeto L.Circle, L.Polygon o L.Circle creado.
 */
export function renderEntityOnMap(entity, popup = null, open = false) {
  const render = entity?.icon
    ? renderMarkerOnMap(entity)
    : entity?.latlngs
    ? renderPolygonGeofence(entity)
    : renderCircleGeofence(entity);

  if (popup) {
    render.bindPopup(popup);
    if (open) render.openPopup();
  }
  return render;
}
/**
 * @brief Método encargado de renderizar una entidad en el mapa.
 *
 * @param entity - Objeto que contiene la información de la geocerca.
 * @param popup - Objeto que contiene la información del popup.
 * @param open - Booleano que indica si el popup se debe abrir o no.
 * @param map - Instancia del mapa donde se renderiza.
 * @returns {Object} Objeto L.Circle, L.Polygon o L.Circle creado.
 */
export function renderEntityOnMapHistorial(
  entity,
  popup = null,
  open = false,
  map = null
) {
  const render = entity?.icon
    ? renderMarkerOnMap(entity, map)
    : entity?.latlngs
    ? renderPolygonGeofence(entity)
    : renderCircleGeofence(entity);

  if (popup) {
    render.bindPopup(popup);
    if (open) render.openPopup();
  }
  return render;
}
/**
 * Método encargado de crear el L.Polygon para la geocerca.
 * @param geofence - Objeto que contiene la información de la geocerca.
 * @returns Objeto L.Polygon creado.
 */
function renderPolygonGeofence(geofence) {
  const { latlngs, color } = geofence;
  return new L.Polygon(latlngs, { color: color, fillColor: color }).addTo(map);
}
/**
 * Método encargado de crear el L.Circle para la geocerca.
 * @param geofence - Objeto que contiene la información de la geocerca.
 * @returns Objeto L.Circle creado.
 */
function renderCircleGeofence(geofence) {
  const { lat, lng, radio, color } = geofence;
  return new L.Circle([lat, lng], radio, {
    color: color,
    fillColor: color,
  }).addTo(map);
}
/**
 * Método encargado de crear el L.Marker para el dispositivo o entidad sobre el mapa.
 * @param marker - Objeto que contiene la información del marcador.
 * @param map - Instancia del mapa donde se renderiza
 * @returns - Objeto L.Marker creado.
 */
function renderMarkerOnMap(marker, map) {
  const { latlng, icon } = marker;
  return new L.Marker([latlng.lat, latlng.lng], { icon: icon }).addTo(map);
}
/**
 * Método encargado de eliminar cualquier entidad del mapa.
 * Las entidades a eliminar pueden ser: marcadores, geocercas o dispositivos.
 * @param entity - Objeto que contiene la información de la entidad a eliminar.
 * @param zoomDefault - Booleano que índica si debe restablecer el zoom por defecto.
 * @param map - Instancia del mapa donde se renderiza
 */
export function removeEntityFromMap(entity, zoomDefault = false, map = null) {
  if (Array.isArray(entity)) {
    entity.forEach((item) => {
      const layerToRemove = item?.marker || item;
      if (map.hasLayer(layerToRemove)) {
        map.removeLayer(layerToRemove);
      }
    });
  }

  if (entity && typeof entity === "object") {
    const layerToRemove = entity?.marker || entity;
    if (map.hasLayer(layerToRemove)) {
      map.removeLayer(layerToRemove);
    }
  }

  if (zoomDefault) {
    map.flyTo([22.63429269, -101.42578125], 5, { animate: true, duration: 2 });
  }
}
/**
 * Método encarcado de editar una ubicación en el mapa.
 * Crea un botón sobre el mapa para editar el marcador actual.
 * @param context - Contexto de la vista donde se encuentra el mapa.
 * @param marker - Objeto que contiene la información del marcador a editar.
 * @param map - Instancia del mapa donde se renderiza
 */
export function editMarkerOnMap(context, marker,map) {
  console.log("context:  ",context);
  console.log("marker:: ",marker);

  if (!map) {
    console.error("No se recibió la instancia del mapa");
    return;
  }
  
  
  const drawLocationLayer = new globalThis.L.FeatureGroup().addTo(map);
  const drawControl = new L.Control.Draw({
    position: "bottomright",
    edit: {
      featureGroup: drawLocationLayer,
      remove: false,
    },
    draw: false,
  });

  map.addControl(drawControl);
  
  new L.Marker([marker.latitud, marker.longitud], {
    icon: saveLocationIcon,
  }).addTo(drawLocationLayer);

  map.flyTo([marker.latitud, marker.longitud], 16);

  context.layerMarker = drawLocationLayer;
  context.drawControl = drawControl;

  map.on(L.Draw.Event.EDITED, (event) => {
    const editedData = onLocationEdited(event);
    context.marcador.latitud = editedData.lat;
    context.marcador.longitud = editedData.lng;
  });
}

/**
 * Limpia el rastro de edición del mapa (marcador temporal y botones de control)
 * @param context - Contexto de Vue
 * @param map - Instancia del mapa
 */
export function clearEditing(context, map) {
  if (!map) return;

  // Quitar la capa de dibujo (el marcador de edición)
  if (context.layerMarker) {
    map.removeLayer(context.layerMarker);
    context.layerMarker = null;
  }

  // Quitar el control (los botones de la esquina)
  if (context.drawControl) {
    map.removeControl(context.drawControl);
    context.drawControl = null;
  }
  
  // Apagar el listener de edición para no acumular eventos
  map.off(L.Draw.Event.EDITED);
}
/**
 * @brief Método encargado de crear el control de edición.
 *
 * @returns {Object} Objeto con la configuración del control y capa de edición.
 */
export function renderControlEdit() {
  const editableLayer = new globalThis.L.FeatureGroup().addTo(map);
  const editControl = new L.Control.Draw({
    position: "bottomright",
    edit: {
      featureGroup: editableLayer,
      remove: false,
      poly: {
        allowIntersection: false,
      },
    },
    draw: false,
  });
  map.addControl(editControl);
  return { control: editControl, layer: editableLayer };
}
/**
 * Método encargado de obtener las nuevas coordenadas del marcador editado.
 * @param event - Evento que se genera al editar el marcador.
 * @returns - Objeto que contiene las nuevas coordenadas del marcador.
 */
function onLocationEdited(event) {
  const layers = event.layers;
  let marker = {};
  layers.eachLayer(function (layer) {
    const latlng = layer.getLatLng();
    marker = {
      lat: Number.parseFloat(latlng.lat).toFixed(8),
      lng: Number.parseFloat(latlng.lng).toFixed(8),
      layer: layer,
    };
  });
  return marker;
}

/**
 * Método encargado de centrar el mapa en una posición específica.
 * @param latlng - Objeto que contiene la latitud y longitud a centrar.
 * @param zoom - Nivel de zoom al que se debe centrar el mapa.
 */
export function focusPositionOnMap(latlng, zoom) {
  map.flyTo([latlng.lat, latlng.lng], zoom);
}

export function nuevoZoom(shapes) {
  if (!shapes || shapes.length === 0) return;

  if (shapes.length === 1) {
    const shape = shapes[0];

    if (shape instanceof L.Circle) {
      const center = shape.getLatLng();
      const radius = shape.getRadius();

      const getZoomLevelForRadius = (radius) => {
        const zoom = 18 - Math.log2(radius / 100);
        return Math.max(1, Math.min(zoom, 18));
      };

      const zoom = getZoomLevelForRadius(radius);
      map.flyTo(center, zoom);
    } else if (shape instanceof L.Marker) {
      const latlng = shape.getLatLng();
      map.flyTo(latlng, 18);
    } else if (typeof shape.getBounds === "function") {
      const bounds = shape.getBounds();
      const center = bounds.getCenter();
      const zoom = map.getBoundsZoom(bounds);
      map.flyTo(center, zoom);
    }
  } else {
    const bounds = L.latLngBounds([]);
    shapes.forEach((shape) => {
      if (typeof shape.getBounds === "function") {
        bounds.extend(shape.getBounds());
      } else if (shape.getLatLng) {
        bounds.extend(shape.getLatLng());
      }
    });

    map.flyToBounds(bounds, {
      padding: [40, 40],
    });
  }
}
